import React from "react";
import PropTypes from "prop-types";
import "./ResultsLoadMorePanel.scss";

/**
 * ResultsLoadMorePanel Component
 *
 * A reusable CTA panel for loading more records in infinite scroll scenarios.
 * Used across Users, Companies, and MainView components.
 *
 * @param {boolean} isLoadingMore - Whether more data is currently being loaded
 * @param {boolean} hasMore - Whether there are more records to load
 * @param {Function} onLoadMore - Callback function when button is clicked. Receives shouldScroll parameter
 * @param {boolean} loading - Whether initial data is loading (hides panel if true)
 * @param {number} itemsCount - Number of items currently displayed (optional, for conditional rendering)
 * @param {boolean} shouldScroll - Whether to scroll after loading (default: true)
 */
const ResultsLoadMorePanel = ({
  isLoadingMore = false,
  hasMore = false,
  onLoadMore,
  loading = false,
  itemsCount = 0,
  shouldScroll = true,
  forceDisableButton = false,
}) => {
  // Hide only when there are truly no rows to show and not in a loading state
  if (!loading && itemsCount === 0) {
    return null;
  }

  const handleClick = () => {
    if (onLoadMore) {
      onLoadMore(shouldScroll);
    }
  };

  const helperText = (loading || isLoadingMore)
    ? "Loading results..."
    : hasMore
    ? `Viewing ${itemsCount.toLocaleString()} result${
        itemsCount === 1 ? "" : "s"
      }`
    : `All ${itemsCount.toLocaleString()} result${
        itemsCount === 1 ? "" : "s"
      } shown`;

  const subtitleText = loading || isLoadingMore
    ? "Fetching records..."
    : hasMore
    ? "Scroll or tap below for more."
    : "That's everything for now.";

  const isButtonDisabled =
    forceDisableButton || !hasMore || isLoadingMore || loading;

  const buttonLabel = loading || isLoadingMore
    ? "Loading..."
    : !hasMore || forceDisableButton
    ? "No new results"
    : "Load more";

  return (
    <section className="load-more">
      <div className="load-more__meta">
        <p className="load-more__title">
          {(isLoadingMore || !hasMore) && (
            <span
              className={`load-more__status-icon${
                isLoadingMore ? " load-more__status-icon--loading" : ""
              }`}
              aria-hidden
            />
          )}
          {helperText}
        </p>
        <p className="load-more__subtitle">{subtitleText}</p>
      </div>

      <button
        className="load-more__cta"
        onClick={handleClick}
        aria-pressed={isLoadingMore}
        aria-busy={isLoadingMore}
        disabled={isButtonDisabled}
      >
        {isLoadingMore && <span className="load-more__spinner" aria-hidden />}
        <span>{buttonLabel}</span>
        <span className="load-more__cta-icon" aria-hidden />
      </button>
    </section>
  );
};

ResultsLoadMorePanel.propTypes = {
  isLoadingMore: PropTypes.bool,
  hasMore: PropTypes.bool,
  onLoadMore: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  itemsCount: PropTypes.number,
  shouldScroll: PropTypes.bool,
  forceDisableButton: PropTypes.bool,
};

export default ResultsLoadMorePanel;


